import React, { lazy, Suspense, useState } from 'react'
import Box from '@mui/material/Box'
import LoadingPage from '../../../pages/LoadingPage'
import CreateEditDivision from '../CreateDivision'
import type { DataObjectList } from '../../../models/DataObject'
import { type Division } from '../../../models/Division'
import { type GridSortItem } from '@mui/x-data-grid'
import DeleteDivisionUnit from '../DeleteDivision'
import ArchiveDivision from '../ArchiveDivision'
import ReportHierarchyTopbarActions from '../ReportHierarchyTopbarActions'
import { DivisionManager } from '../../../APIManager'
import { exportCsv } from '../../../utils/exportCsv'
import { notification, closeNotification } from '../../../components/Notifications'
import ExportProcessingModal from '../../../components/ExportProcessingModal'
import { useTaskStatus } from '../../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../../components/ExportBackgroundTaskRunningModal'

interface ReportHierarchyFeatureProps {
  list: DataObjectList<Division>
  handleRowsPerPageChange: (arg: number) => void
  handlePageChange: (arg: number) => void
  onSearch: (arg: string) => void
  onSortModelChange: (sortModel: GridSortItem) => void
  loading: boolean
  categoryName?: string
  allowedActions?: string[]
  breadcrumbId?: string
  params?: Record<string, any>
  exportCsv?: () => void
  exportCSVLoading?: boolean
}

const AppTable = lazy(async () => await import('../../../components/CustomTable'))
const DivisionTable: React.FC<ReportHierarchyFeatureProps> = ({ list, handleRowsPerPageChange, handlePageChange, onSearch, onSortModelChange, loading, categoryName, allowedActions, breadcrumbId, params }): JSX.Element => {
  const [exportLoading, setExportLoading] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')

  const {
    taskId: uploadTaskId,
    setTaskId: setUploadTaskId,
    showBackgroundModal: showUploadBlockedModal,
    setShowBackgroundModal: setShowUploadBlockedModal,
    isCheckingStatus: isUploadRunning
  } = useTaskStatus('uploadTask_reportHierarchy')

  const {
    taskId: exportTaskId,
    setTaskId: setExportTaskId,
    showBackgroundModal: showExportBlockedModal,
    setShowBackgroundModal: setShowExportBlockedModal,
    isCheckingStatus: isExportRunning,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_reportHierarchy')

  const isAnyTaskRunning = isUploadRunning || isExportRunning

  const openBlockedModal = (): void => {
    if (isUploadRunning) setShowUploadBlockedModal(true)
    else setShowExportBlockedModal(true)
  }

  const rows = list?.data
  const columns = Array.isArray(list?.keys)
    ? list.keys.filter((key) => key.field !== 'children_count')
    : []

  const headerName: string = list?.header?.headerName
  const academicUnit = categoryName ?? rows[0]?.data?.categoryName

  const extraColumnProps = {
    flex: 1
  }
  const extraModalProps = {
    width: 'sm'
  }

  const handleExportChildren = async (): Promise<void> => {
    if (!breadcrumbId) return
    if (isUploadRunning) { openBlockedModal(); return }
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    setExportLoading(true)
    const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
    try {
      const blob = await DivisionManager.exportChildrenCSV(breadcrumbId)
      if (blob?.status === 202) {
        if (blob?.data?.taskId) {
          setExportTaskId(blob.data.taskId)
        }
        closeNotification(toastKey)
        setExportModalMessage(blob?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      exportCsv({ data: blob.data, headers: blob.headers, loadingToastKey: toastKey })
    } catch (e) {
      closeNotification(toastKey)
      notification('Export failed', { variant: 'error' })
    } finally {
      setExportLoading(false)
    }
  }

  const topbarActionsDropdownMenu = [
    {
      text: 'Export reporting hierarchy (.csv)',
      action: () => { void handleExportChildren() }
    }
  ]

  return (
    <Box width={'100%'}>
      <Suspense fallback={<LoadingPage />}>

        {columns !== undefined && rows !== undefined &&
          <AppTable
            loading={loading}
            topbarTitle={headerName}
            createAction={<CreateEditDivision isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            rows={rows}
            topbarActionsDropdownMenu={topbarActionsDropdownMenu}
            exportButtonProps={{ loading: exportLoading || isAnyTaskRunning, disabled: exportLoading || isAnyTaskRunning }}
            list={list}
            columns={columns}
            editAction={<CreateEditDivision isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            deleteAction={<DeleteDivisionUnit isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            archiveAction={<ArchiveDivision isTaskRunning={isAnyTaskRunning} onTaskBlock={openBlockedModal} />}
            featureName={academicUnit}
            extraColumnProps={extraColumnProps}
            extraModalProps={extraModalProps}
            onSearch={onSearch}
            onSortModelChange={onSortModelChange}
            handlePageChange={(page: number) => { handlePageChange(page) }}
            handleRowsPerPageChange={(pageSize: number) => { handleRowsPerPageChange(pageSize) }}
            customTopbarAction={
              <ReportHierarchyTopbarActions
                categoryName={categoryName ?? academicUnit}
                allowedActions={allowedActions}
                breadcrumbId={breadcrumbId}
                params={params}
                setUploadTaskId={setUploadTaskId}
                isTaskRunning={isAnyTaskRunning}
                onTaskBlock={openBlockedModal}
              />
            }
          />
        }
      </Suspense>
      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showExportBlockedModal}
        onClose={() => { setShowExportBlockedModal(false) }}
        taskId={exportTaskId ?? ''}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showUploadBlockedModal}
        onClose={() => { setShowUploadBlockedModal(false) }}
        taskId={uploadTaskId ?? ''}
      />
    </Box>
  )
}

export default DivisionTable
